<template>
    <div>
        <b-modal
            id="filterDateRangeForm"
            ref="filterDateRangeForm"
            :lazy="true"
            title-tag="h2"
            :title="this.title"
        >
            <div v-if="message" class="alert alert-warning" role="alert">
                <i class="cus-error"></i>
                {{ message }}
            </div>

            <b-form-group
                v-if="isAppointmentLogReport"
                :label-for="`client_id`"
                :label-cols="3"
                label="Client:"
            >
                <vet-select
                    id="client_id"
                    v-model="data.client"
                    v-validate="''"
                    module="clients-include-inactive"
                    placeholder="Client Name (optional)"
                    name="client"
                    :showMenu="false"
                />
            </b-form-group>
            <b-form-group
                v-if="isAppointmentLogReport"
                :label-for="`patient_id`"
                :label-cols="3"
                label="Patient:"
            >
                <vet-select
                    :showMenu="false"
                    id="patient_id"
                    v-model="data.patient"
                    :disabled="!data.client"
                    v-validate
                    :client-id="data.client ? data.client.id : null"
                    module="client-patient"
                    placeholder="Patient Name (optional)"
                    label="name"
                    name="patient"
                />
            </b-form-group>

            <b-form-group
                v-if="isShowProvider"
                :label-for="`provider`"
                label-cols="3"
                label="Provider:"
            >
                <v-select
                    id="provide_id"
                    class="show-actions"
                    v-model="data.provider"
                    name="provider_id"
                    v-validate="'required'"
                    :label="'name'"
                    multiple
                    :placeholder="
                        optionLoading.providerList
                            ? 'Loading...'
                            : 'Provider (required)'
                    "
                    :options="providerFilter"
                    :loading="optionLoading.providerList"
                >
                </v-select>
            </b-form-group>

            <b-form-group
                v-if="isShowModuleOption"
                :label-for="`module`"
                :label-cols="3"
                label="Module:"
            >
                <v-select
                    id="module_id"
                    v-model="data.module"
                    name="module_id"
                    :label="'text'"
                    multiple
                    :options="moduleFilter"
                >
                </v-select>
            </b-form-group>

            <b-form-group
                v-if="isShowItemStatus"
                :label-for="`status`"
                :label-cols="3"
                label="Item Status:"
            >
                <b-form-select
                    v-model="data.status"
                    :options="statusOptions"
                    name="status"
                ></b-form-select>
            </b-form-group>

            <SelectItemClassCategory
                ref="selectItemClassCategory"
                v-if="isShowItemCategory || isShowItemClass"
                :data="data"
                @update-data="handleChildChanges"
                :isShowItemCategory="isShowItemCategory"
                :isShowItemClass="isShowItemClass"
            />

            <b-form-group
                v-if="isShowAsOf"
                :label-for="`asOf`"
                label-cols="3"
                label="As Of:"
            >
                <date-picker
                    v-validate="'required'"
                    :config="datePickerOptions"
                    :value="data.asOf"
                    :class="{ 'border-danger': errors.has('asOf') }"
                    name="asOf"
                    @dp-change="dateChange($event, 'asOf')"
                ></date-picker>
            </b-form-group>

            <b-form-group
                v-if="isShowFilter"
                :label-for="`filterBy`"
                label="Display:"
                label-cols="3"
            >
                <v-select
                    v-model="filterBySelected"
                    :options="filterByOptions"
                    name="filterBy"
                    :clearable="false"
                    class="show-actions"
                />
            </b-form-group>

            <b-form-group
                v-if="isShowDateRange || isShowDateTimeRange"
                :label-for="`date_range`"
                label="Date Range:"
                label-cols="3"
            >
                <v-select
                    v-model="defaultSelected"
                    :options="dateRangeOptions"
                    name="date-range"
                    item-text="label"
                    item-value="code"
                    @input="rangeChange($event)"
                    :clearable="false"
                    class="show-actions"
                />
            </b-form-group>

            <b-form-group
                v-if="isShowDateRange"
                :label-for="`start`"
                label
                label-cols="3"
            >
                <div class="form-inline mb-1">
                    <date-picker
                        v-validate="'required'"
                        :config="datePickerOptions"
                        :value="data.start"
                        :class="{ 'border-danger': errors.has('start') }"
                        class="form-control col-5"
                        name="start"
                        @dp-change="dateChange($event, 'start')"
                    ></date-picker>
                    <span class="col-2" style="text-align: center;">-</span>
                    <date-picker
                        v-validate="'required'"
                        :config="datePickerOptions"
                        :value="data.end"
                        :class="{ 'border-danger': errors.has('end') }"
                        class="form-control col-5"
                        name="end"
                        @dp-change="dateChange($event, 'end')"
                    ></date-picker>
                </div>
            </b-form-group>
            <FilterWriteOffs
                v-if="reportId === CREPORT.TYPE.WRITE_OFFS"
                ref="filterWriteOffs"
                :data="data"
                @update-data="handleChildChanges"
            />
            <b-form-group
                v-if="isShowDateRangeDayMonth"
                :label-for="`From`"
                label="From:"
                label-cols="3"
            >
                <div class="form-inline justify-content-end">
                    <b-form-select
                        plain
                        class="col-5 w-100"
                        v-model="data.fromMonth"
                        :options="monthOptions"
                        name="dateRangeMonth"
                    ></b-form-select>
                    <b-form-select
                        plain
                        class="col-5 w-100 ml-2"
                        v-model="data.fromDay"
                        :options="dayOptionsFrom"
                        name="dateRangeDay"
                    ></b-form-select>
                </div>
            </b-form-group>
            <b-form-group
                v-if="isShowDateRangeDayMonth"
                :label-for="`To`"
                label="To:"
                label-cols="3"
            >
                <div class="form-inline justify-content-end">
                    <b-form-select
                        plain
                        class="col-5 w-100"
                        v-model="data.toMonth"
                        :options="monthOptions"
                        name="dateRangeMonth"
                    ></b-form-select>
                    <b-form-select
                        plain
                        class="col-5 w-100 ml-2"
                        v-model="data.toDay"
                        :options="dayOptionsTo"
                        name="dateRangeDay"
                    ></b-form-select>
                </div>
            </b-form-group>

            <b-form-group v-if="isShowDateTimeRange" label label-cols="3">
                <vet-datetime
                    id="start"
                    :config="datePickerOptions"
                    class="mb-1"
                    style="margin-right: -0.2rem;"
                    v-model="data.start"
                    v-validate="'required'"
                    :placeholder="{
                        date: 'Date (required)',
                        time: 'Time (required)'
                    }"
                    container="#filterDateRangeForm"
                    rules="required"
                    name="start"
                    format="moment"
                ></vet-datetime>
                <vet-datetime
                    id="end"
                    :config="datePickerOptions"
                    style="margin-right: -0.2rem;"
                    v-model="data.end"
                    v-validate="'required'"
                    :placeholder="{
                        date: 'Date (required)',
                        time: 'Time (required)'
                    }"
                    container="#filterDateRangeForm"
                    rules="required"
                    name="end"
                    format="moment"
                ></vet-datetime>
            </b-form-group>

            <b-form-group
                v-if="isShowRemindersCategory"
                :label-for="`remindersCategory`"
                :label-cols="3"
                label="Category:"
            >
                <b-form-select
                    v-model="remindersCategoryMode"
                    :options="remindersCategoryModeOptions"
                    name="remindersCategoryMode"
                ></b-form-select>

                <v-select
                    v-show="remindersCategoryMode === 2"
                    class="mt-1 show-actions"
                    :placeholder="
                        optionLoading.remindersCategoryOptions
                            ? 'Loading...'
                            : 'Category (required)'
                    "
                    :options="reminderCatOptions"
                    multiple
                    label="value"
                    v-model="data.remindersCategory"
                    :loading="optionLoading.remindersCategoryOptions"
                ></v-select>
            </b-form-group>

            <b-form-group
                v-if="isShowRemindersTypes"
                :label-for="`remindersTypes`"
                :label-cols="3"
                label="Types:"
            >
                <v-select
                    class="mt-1"
                    placeholder="Type (required)"
                    :options="reminderOptionsFiltered"
                    multiple
                    v-model="data.remindersType"
                ></v-select>
            </b-form-group>

            <b-form-group
                v-if="isShowNextVisit"
                :label-for="`remindersNextVisit`"
                :label-cols="3"
                label="Next Visit:"
            >
                <b-form-select
                    v-model="remindersNextVisit"
                    :options="[
                        {
                            value: 1,
                            text: 'Include if next visit scheduled'
                        },
                        {
                            value: 0,
                            text: 'Exclude if next visit scheduled'
                        }
                    ]"
                    name="remindersNextVisit"
                ></b-form-select>
            </b-form-group>

            <b-form-group
                v-if="isShowPatientStatus"
                :label-for="`patientStatus`"
                :label-cols="3"
                label="Patient Status:"
            >
                <v-select
                    class="mt-1"
                    placeholder="Status (required)"
                    :options="patientStatusOptionsFiltered"
                    multiple
                    required
                    v-model="data.patientStatus"
                ></v-select>
            </b-form-group>

            <b-form-group
                v-if="isShowItemOptions"
                :label-for="`items`"
                :label-cols="3"
                label="Items:"
            >
                <b-form-select
                    v-model="itemShowMode"
                    :options="itemShowModeOptions"
                    name="itemShowMode"
                    @input="changeItemShowMode"
                />
                <v-select
                    placeholder="Items (required)"
                    v-show="itemShowMode === 2"
                    class="mt-1"
                    id="items"
                    v-model="data.items"
                    name="items"
                    label="name"
                    multiple
                    :options="itemOptionsFilter"
                    @search="onSearchItem"
                >
                    <template v-slot:option="option">
                        <span style="white-space: normal;">
                            {{ option.name }}
                        </span>
                    </template>
                    <template v-slot:no-options="{ search }">
                        <div class="pl-1">
                            <span v-if="search === ''">
                                Type to search
                            </span>
                            <span v-else-if="isItemSearching">
                                Searching...
                            </span>
                            <span v-else>
                                No result
                            </span>
                        </div>
                    </template>
                </v-select>
            </b-form-group>

            <SelectProvider
                v-if="isShowProviderOptions"
                :data="data"
                @update-data="handleChildChanges"
            />

            <CategoryWithSales
                v-if="isShowCategorySales"
                :dataCategorySales="data.categorySales"
                @update-data="handleChildChanges"
            />

            <b-form-group
                v-if="reportId === CREPORT.TYPE.DIAGNOSIS_SUMMARY_LIST"
                :label-for="`diagnosis_options`"
                label="Diagnosis:"
                label-cols="3"
            >
                <b-form-select
                    v-model="diagnosisShowMode"
                    :options="[
                        {
                            value: 0,
                            text: 'Display all diagnoses'
                        },
                        {
                            value: 1,
                            text: 'Display specific diagnoses'
                        }
                    ]"
                    @input="changeDiagnosisShowMode"
                    name="diagnosis_options"
                ></b-form-select>

                <v-select
                    placeholder="Diagnosis (required)"
                    v-show="diagnosisShowMode === 1"
                    class="mt-1"
                    id="diagnosis"
                    v-model="data.diagnosis"
                    :class="{
                        'border-danger': errors.has('diagnosis')
                    }"
                    v-validate="'required'"
                    name="diagnosis"
                    label="name"
                    multiple
                    :options="diagnosisFilter"
                >
                </v-select>
            </b-form-group>

            <b-form-group
                v-if="reportId === CREPORT.TYPE.DIAGNOSIS_SUMMARY_LIST"
                :label-for="`status_diagnosis`"
                label="Status:"
                label-cols="3"
            >
                <b-form-select
                    v-model="data.diagnosisStatus"
                    :options="[
                        {
                            value: 0,
                            text: 'Open and Closed'
                        },
                        {
                            value: 1,
                            text: 'Open Only'
                        },
                        {
                            value: 2,
                            text: 'Closed Only'
                        }
                    ]"
                    name="status_diagnosis"
                ></b-form-select>
            </b-form-group>

            <b-form-group
                v-if="
                    reportId === CREPORT.TYPE.DIAGNOSIS_SUMMARY_LIST ||
                    reportId === CREPORT.TYPE.RABIES_SUMMARY
                "
                :label-for="`species`"
                label="Species:"
                label-cols="3"
            >
                <v-select
                    :placeholder="
                        optionLoading.speciesList
                            ? 'Loading...'
                            : 'Species (required)'
                    "
                    class="mt-1 show-actions"
                    id="species"
                    name="species"
                    label="name"
                    multiple
                    v-model="data.species"
                    :class="{ 'border-danger': errors.has('species') }"
                    v-validate="'required'"
                    :options="speciesFilter"
                    :loading="optionLoading.speciesList"
                >
                </v-select>
            </b-form-group>

            <b-form-group
                v-if="reportId === CREPORT.TYPE.RABIES_SUMMARY"
                :label-for="`city`"
                label="City:"
                label-cols="3"
            >
                <v-select
                    :placeholder="
                        optionLoading.cityList
                            ? 'Loading...'
                            : 'City (optional)'
                    "
                    class="mt-1 show-actions"
                    id="city"
                    name="city"
                    label="name"
                    multiple
                    v-model="data.city"
                    :loading="optionLoading.cityList"
                    :options="cityFilter"
                >
                </v-select>
            </b-form-group>

            <ShowAllCategoriesCheckbox
                v-if="
                    reportId === CREPORT.TYPE.PRODUCTION_CREDIT_BY_PAYMENTS ||
                    reportId === CREPORT.TYPE.PRODUCTION_CREDIT_BY_SALES
                "
                :reportId="reportId"
                :value="data.isShowAllSalesCategory"
                @update-data="handleChildChanges"
            />

            <IncludeRefillSalesCheckbox
                v-if="
                    reportId === CREPORT.TYPE.PRODUCTION_CREDIT_BY_PAYMENTS ||
                    reportId === CREPORT.TYPE.PRODUCTION_CREDIT_BY_SALES
                "
                :reportId="reportId"
                :value="data.isIncludeRefillSales"
                @update-data="handleChildChanges"
            />

            <ExcludeEligibilityFeeCheckbox
                v-if="
                    reportId === CREPORT.TYPE.PRODUCTION_CREDIT_BY_PAYMENTS ||
                    reportId === CREPORT.TYPE.PRODUCTION_CREDIT_BY_SALES
                "
                :value="data.isExcludeEligibilityFee"
                :tooltipMessage="TOOLTIP.MESSAGE.PROD_CREDIT"
                @update-data="handleChildChanges"
            />

            <b-form-group
                v-if="isShowHomeLocationOptions && isRecordSharing"
                label-for="homeLocation"
                label-cols="3"
                label="Home Location:"
            >
                <b-select v-model="homeLocation">
                    <option value="" selected>
                        All Location
                    </option>
                    <option
                        v-for="location in homeLocations"
                        :key="location.id"
                        :value="location.id"
                    >
                        {{ location.name }}
                    </option>
                </b-select>
            </b-form-group>

            <slot name="extra-content"></slot>

            <div slot="modal-footer" class="w-100">
                <b-button
                    v-if="!loading && !isExporting"
                    class="float-left"
                    variant="secondary"
                    @click="$refs.filterDateRangeForm.hide()"
                    >Cancel</b-button
                >
                <div class="float-right">
                    <img
                        v-if="loading || isExporting"
                        src="assets/ajax-loader.gif"
                    />
                    <b-button-group
                        v-else-if="isShowDownloadButton || isShowPrintButton"
                    >
                        <b-dropdown
                            @click="filterDate"
                            left
                            split
                            text="Update"
                            variant="primary"
                        >
                            <b-dropdown-item
                                v-if="isShowDownloadButton"
                                @click="handleExport()"
                                >Download</b-dropdown-item
                            >
                            <b-dropdown-item
                                v-if="isShowPrintButton"
                                @click="handlePrint()"
                                >Print</b-dropdown-item
                            >
                        </b-dropdown>
                    </b-button-group>
                    <b-button-group
                        v-else-if="
                            [
                                CREPORT.TYPE.SALES_BY_PROVIDER_DETAILS,
                                CREPORT.TYPE.PAYMENT_BY_PROVIDER
                            ].includes(reportId)
                        "
                    >
                        <b-dropdown
                            @click="filterDate"
                            left
                            split
                            text="Update"
                            variant="primary"
                        >
                            <b-dropdown-item @click="handleExport('S')"
                                >Download Summary</b-dropdown-item
                            >
                            <b-dropdown-item @click="handleExport('D')"
                                >Download Detail</b-dropdown-item
                            >
                            <b-dropdown-item @click="handleExport('F')"
                                >Download Complete</b-dropdown-item
                            >
                            <b-dropdown-item @click="handlePrint()"
                                >Print</b-dropdown-item
                            >
                        </b-dropdown>
                    </b-button-group>
                    <b-button-group
                        v-else-if="
                            reportId == CREPORT.TYPE.DELETED_TRANSACTIONS
                        "
                    >
                        <b-dropdown
                            @click="filterDate"
                            left
                            split
                            text="Update"
                            variant="primary"
                        >
                            <b-dropdown-item @click="handleExport('F')"
                                >Download</b-dropdown-item
                            >
                            <b-dropdown-item @click="handlePrint()"
                                >Print</b-dropdown-item
                            >
                        </b-dropdown>
                    </b-button-group>
                    <b-button
                        v-else
                        variant="primary"
                        @click="filterDate"
                        :disabled="isSubmitDisabled"
                    >
                        Update
                    </b-button>
                </div>
            </div>
        </b-modal>
    </div>
</template>
<script>
import vSelect from "vue-select";
import vetDatetime from "@/module/commons/date-time-picker.vue";
import vetSelect from "@/module/commons/vet-select.vue";
import CREPORT from "@/constants/report";
import TOOLTIP from "@/constants/tooltip-message";
import ExportReportMixin from "@/mixins/export-report";
import { DateTime } from "luxon";
import ItemServices from "@/services/items.js";
import {
    remove,
    find,
    get,
    difference,
    debounce,
    differenceBy,
    map,
    sortBy,
    orderBy,
    isNil,
    includes
} from "lodash";

const PATIENT_STATUS_ACTIVE = 1;
const PATIENT_STATUS_INACTIVE = 2;
const PATIENT_STATUS_DECEASED = 3;

export default {
    mixins: [ExportReportMixin],
    components: {
        vSelect,
        vetDatetime,
        vetSelect,
        // Treeselect,
        FilterWriteOffs: () =>
            import("@/module/report/module/filter-write-offs.vue"),
        SelectProvider: () =>
            import("@/module/report/module/filter-fields/select-provider.vue"),
        SelectItemClassCategory: () =>
            import(
                "@/module/report/module/filter-fields/select-item-class-category.vue"
            ),
        CategoryWithSales: () =>
            import(
                "@/module/report/module/filter-fields/category-with-sales.vue"
            ),
        ShowAllCategoriesCheckbox: () =>
            import(
                "@/module/report/module/filter-fields/show-all-categories-checkbox.vue"
            ),
        IncludeRefillSalesCheckbox: () =>
            import(
                "@/module/report/module/filter-fields/include-refill-sales-checkbox.vue"
            ),
        ExcludeEligibilityFeeCheckbox: () =>
            import(
                "@/module/report/module/filter-fields/exclude-eligibility-fee-checkbox.vue"
            )
    },
    data() {
        return {
            params: null,
            optionLoading: [],
            CREPORT: CREPORT,
            reportId: null,
            datePickerOptionDefault: null,
            isShowProvider: false,
            isShowCategorySales: false,
            isShowItemClass: false,
            isShowItemCategory: false,
            isShowDateTimeRange: false,
            isShowDateRange: true,
            isShowAsOf: false,
            isShowItemStatus: false,
            isShowModuleOption: false,
            isShowItemOptions: false,
            isShowProviderOptions: false,
            isShowRemindersCategory: false,
            isShowNextVisit: false,
            isShowDateRangeDayMonth: false,
            isShowFilter: false,
            isShowHomeLocationOptions: false,
            isShowPatientStatus: false,
            isGreyOutButton: false,
            homeLocation: "",
            loading: false,
            skipUpdate: false,
            message: "",
            setting: {},
            providerList: [],
            itemClass: 0,
            category: [],
            categoryArray: [],
            startDateTime: null,
            itemShowMode: 1,
            remindersCategoryMode: 1,
            remindersCategoryOptions: [],
            isShowRemindersTypes: false,
            remindersTypesOptions: [],
            remindersNextVisit: 1,
            diagnosisShowMode: 0,
            diagnosisList: [],
            speciesList: [],
            cityList: [],
            dayOptionsFrom: Array.from(Array(31).keys(), x => x + 1),
            dayOptionsTo: Array.from(Array(31).keys(), x => x + 1),
            data: {
                start: moment().tz(this._timeZone),
                end: moment().tz(this._timeZone),
                ignoreFormat: false,
                provider: null,
                client: [],
                patient: [],
                itemClass: 0,
                category: [],
                status: 2,
                asOf: moment().tz(this._timeZone),
                applyTo: 0,
                module: null,
                items: [],
                remindersCategory: [],
                remindersType: [],
                diagnosis: [],
                diagnosisStatus: 0,
                species: [],
                city: [],
                fromMonth: null,
                fromDay: null,
                toMonth: null,
                toDay: null,
                categorySales: true,
                isShowAllSalesCategory: 0,
                isIncludeRefillSales: 1,
                isExcludeEligibilityFee: 0, // TODO: remove this
                patientStatus: [],
                writeOffsMin: null,
                writeOffsMax: null,
                providerShowMode: 1
            },
            remindersCategoryModeOptions: [
                {
                    value: 1,
                    text: "Display all categories"
                },
                {
                    value: 2,
                    text: "Display specific categories"
                }
            ],
            itemType: null,
            itemShowModeOptions: [
                {
                    value: 1,
                    text: "Display all items"
                },
                {
                    value: 2,
                    text: "Display specific items"
                }
            ],
            providerShowModeOptions: [
                {
                    value: 1,
                    text: "Display all providers"
                },
                {
                    value: 2,
                    text: "Display specific providers"
                }
            ],
            datePickerOptions: {
                format: null,
                timeZone: ""
            },
            defaultSelected: [],
            statusOptions: [
                {
                    value: 2,
                    text: "Display all items"
                },
                {
                    value: 1,
                    text: "Display active items only"
                },
                {
                    value: 0,
                    text: "Display inactive items only"
                }
            ],
            dateRangeOptions: [
                {
                    label: "Custom - The date range selected by the clinic",
                    code: "1"
                },
                {
                    label: "This week - The current week to date",
                    code: "2"
                },
                {
                    label: "This month - The current calendar month",
                    code: "3"
                },
                {
                    label: "This quarter - The current calendar quarter",
                    code: "4"
                },
                {
                    label: "This year - The current calendar year",
                    code: "5"
                },
                {
                    label: "Last week - The last calendar week",
                    code: "6"
                },
                {
                    label: "Last month - The last calendar month",
                    code: "7"
                },
                {
                    label: "Last quarter - The last calendar quarter",
                    code: "8"
                },
                {
                    label: "Last year - The last calendar year",
                    code: "9"
                }
            ],
            moduleOptions: [
                {
                    value: 1,
                    text: "Schedule"
                },
                {
                    value: 2,
                    text: "Patient"
                },
                {
                    value: 3,
                    text: "Client"
                },
                {
                    value: 4,
                    text: "Contact"
                },
                {
                    value: 5,
                    text: "Inventory"
                },
                {
                    value: 6,
                    text: "Billing"
                },
                {
                    value: 7,
                    text: "Settings"
                },
                {
                    value: 8,
                    text: "Tasks"
                }
            ],
            defaultOptions: [],
            filterByOptions: [
                {
                    label: "Monthly",
                    value: "month"
                },
                {
                    label: "Weekly",
                    value: "week"
                }
            ],
            patientStatusOptions: [
                {
                    value: PATIENT_STATUS_ACTIVE,
                    label: "Active"
                },
                { value: PATIENT_STATUS_INACTIVE, label: "Inactive" },
                { value: PATIENT_STATUS_DECEASED, label: "Deceased" }
            ],
            filterBySelected: [],
            itemOptions: [],
            isItemSearching: false,
            title: "Filter Date",
            isFilterDateMonthOnly: false,
            TOOLTIP
        };
    },
    watch: {
        remindersCategoryMode(value) {
            if (value === 1) {
                this.data.remindersCategory = []; // clear selected category if any
            }
        },
        "data.fromMonth"(value) {
            this.dayOptionsFrom = this.changeDaysOptions(value);
            if (this.data.fromDay > this.dayOptionsFrom.length) {
                this.data.fromDay = 1;
            }
        },
        "data.toMonth"(value) {
            this.dayOptionsTo = this.changeDaysOptions(value);
            if (this.data.toDay > this.dayOptionsTo.length) {
                this.data.toDay = 1;
            }
        }
    },
    computed: {
        reminderOptionsFiltered() {
            return differenceBy(
                this.reminderOptions,
                this.data.remindersType,
                "value"
            );
        },
        reminderOptions() {
            // if option selected, then hide from the options
            return this.remindersTypesOptions.filter(
                o => this.data.remindersType.indexOf(o) < 0
            );
        },
        patientStatusOptionsFiltered() {
            return differenceBy(
                this.patientStatusOptions,
                this.data.patientStatus,
                "value"
            );
        },
        reminderCatOptions() {
            // if option selected, then hide from the options
            return this.remindersCategoryOptions.filter(
                o => this.data.remindersCategory.indexOf(o) < 0
            );
        },
        providerFilter() {
            return differenceBy(this.providerList, this.data.provider, "value");
        },
        diagnosisFilter() {
            return differenceBy(
                this.diagnosisList,
                this.data.diagnosis,
                "value"
            );
        },
        speciesFilter() {
            return differenceBy(this.speciesList, this.data.species, "value");
        },
        cityFilter() {
            return differenceBy(this.cityList, this.data.city, "value");
        },
        moduleFilter() {
            return difference(this.moduleOptions, this.data.module);
        },
        itemOptionsFilter() {
            return differenceBy(this.itemOptions, this.data.items, "raw_id");
        },
        isSubmitDisabled() {
            switch (this.reportId) {
                case CREPORT.TYPE.SALES_BY_PROVIDER_DETAILS:
                    return (
                        (this.isShowProviderOptions &&
                            this.data.providerShowMode === 2 &&
                            (!this.data.provider ||
                                this.data.provider.length === 0)) ||
                        (this.data.applyTo === 1 &&
                            (!this.data.category ||
                                this.data.category.length === 0))
                    );
                case CREPORT.TYPE.INVENTORY_VALUE_COGS:
                    return (
                        this.data.applyTo === 1 &&
                        (!this.data.category || this.data.category.length === 0)
                    );
                case CREPORT.TYPE.IN_PROCESS_MEDICAL_NOTES:
                    return (
                        !this.data.provider || this.data.provider.length === 0
                    );
                case CREPORT.TYPE.CONTROLLED_SUBSTANCE_LOG:
                case CREPORT.TYPE.UNIQUE_DISPENSING_LOG:
                    return (
                        (this.isShowProviderOptions &&
                            this.data.providerShowMode === 2 &&
                            (!this.data.provider ||
                                this.data.provider.length === 0)) ||
                        (this.isShowItemOptions &&
                            this.itemShowMode === 2 &&
                            (!this.data.items || this.data.items.length === 0))
                    );
                case CREPORT.TYPE.DIAGNOSIS_SUMMARY_LIST:
                    return (
                        (this.diagnosisShowMode == 1 &&
                            this.data.diagnosis.length == 0) ||
                        this.data.species.length == 0
                    );
                case CREPORT.TYPE.RABIES_SUMMARY:
                    return this.data.species.length === 0;
                case CREPORT.TYPE.SALES_BY_ITEM:
                    return (
                        this.isShowItemOptions &&
                        this.itemShowMode === 2 &&
                        (!this.data.items || this.data.items.length === 0)
                    );
                case CREPORT.TYPE.REMINDERS_DETAIL:
                    return (
                        (this.remindersCategoryMode === 2 &&
                            (!this.data.remindersCategory ||
                                this.data.remindersCategory.length === 0)) ||
                        !this.data.remindersType ||
                        this.data.remindersType.length === 0 ||
                        (this.isShowPatientStatus &&
                            this.data.patientStatus.length === 0)
                    );
                default:
                    return false;
            }
        },
        monthOptions() {
            let months = [];
            for (let index = 1; index <= 12; index++) {
                months.push({
                    value: index,
                    text: DateTime.fromObject({ month: index }).monthLong
                });
            }
            return months;
        },
        isAppointmentLogReport() {
            return CREPORT.TYPE.APPOINTMENT_LOG == this.$route.params.id;
        },
        homeLocations() {
            return this.$root.setting.listGroupBusiness ?? [];
        },
        isRecordSharing() {
            return this.$root.setting.isRecordSharing ?? false;
        },
        isShowPrintButton() {
            return [CREPORT.TYPE.SALES_BY_PROVIDER_SUMMARY].includes(
                this.reportId
            );
        },
        isShowDownloadButton() {
            return (
                [CREPORT.TYPE.SALES_BY_PROVIDER_SUMMARY].includes(
                    this.reportId
                ) || this.$route.name == "custom_report"
            );
        }
    },
    methods: {
        convertToBoolean(value) {
            return isNil(value)
                ? true
                : includes([true, "true", 1, "1"], value);
        },
        changeDaysOptions(value) {
            let totalDays = 31;
            if (value == 2) {
                totalDays = 29;
            } else if (
                (value % 2 == 0 && value <= 7) ||
                (value % 2 != 0 && value > 7)
            ) {
                totalDays = 30;
            }
            return Array.from(Array(totalDays).keys(), x => x + 1);
        },
        async fetchRemindersCategoryOptions() {
            this.optionLoading.remindersCategoryOptions = true;

            let res = await this.$http.get(
                `${process.env.BARRAMUNDI}inventory/category-by-type`
            );

            if (
                res.data.response.message &&
                res.data.response.message.code === 200
            ) {
                let options = [
                    {
                        id: "Appointment",
                        value: "Appointment"
                    },
                    {
                        id: "Boarding",
                        value: "Boarding"
                    },
                    ...res.data.response.resources
                ];

                // sort by value
                options = orderBy(options, ["value"], ["asc"]);
                this.remindersCategoryOptions = options;
            } else {
                this.remindersCategoryOptions = [];
            }
            delete this.optionLoading["remindersCategoryOptions"];
        },
        changeDiagnosisShowMode(value) {
            if (value === 0) {
                this.data.diagnosis = null; // clear
            }
        },
        changeItemShowMode(value) {
            if (value === 1) {
                this.data.items = []; // clear
            }
        },
        onSearchItem(query) {
            if (query.length > 0) {
                this.isItemSearching = true;
                this.searchItem(
                    query,
                    this.data.itemClass,
                    this.data.itemType,
                    this
                );
            }
        },
        searchItem: debounce(async (query, itemClassParam, itemType, self) => {
            const params = {
                query: query,
                item_class: itemClassParam,
                item_type: itemType
            };
            const res = await ItemServices.getItemsWithSearch(params);

            if (res.message && res.message.code === 200) {
                // this endpoint returns results in an Object instead of array.
                // so we have to remove some keys and convert the object to array,
                // before we can pass to vue-select as dropdown options.
                let results = res;
                delete results.message;
                delete results.total;

                let options = Object.values(results);
                self.itemOptions = options;
            } else {
                self.itemOptions = [];
            }

            self.isItemSearching = false;
        }, 350),
        open(params) {
            console.log("params", params);
            this.params = params;
            this.optionLoading = [];

            this.reportId = params.reportId || null;
            this.title = params.filterTitle || this.title;

            this.isShowItemOptions = params.isShowItemOptions || false;
            this.itemShowMode = params.itemShowMode || 1;
            this.data.items = params.items || [];
            this.data.provider = params.provider || null;
            this.data.itemType = params.itemType || null;

            this.isShowProviderOptions = params.isShowProviderOptions || false;
            this.data.providerShowMode = params.providerShowMode || 1;

            this.isShowRemindersCategory =
                params.isShowRemindersCategory || false;
            this.remindersCategoryMode = params.remindersCategoryMode || 1;
            this.isShowRemindersTypes = params.isShowRemindersTypes || false;

            this.isShowNextVisit = params.isShowNextVisit || false;

            this.isShowProvider = params.isShowProvider ? true : false;
            this.isShowCategorySales = params.isShowCategorySales
                ? true
                : false;
            this.isShowItemClass = params.isShowItemClass ? true : false;
            this.isShowItemCategory = params.isShowItemCategory ? true : false;
            this.isShowDateRange = params.isShowDateRange ? true : false;
            this.isShowDateTimeRange = params.isShowDateTimeRange
                ? true
                : false;
            this.isShowAsOf = params.isShowAsOf ? true : false;
            this.isShowItemStatus = params.isShowItemStatus ? true : false;
            this.isShowModuleOption = params.isShowModuleOption ? true : false;
            this.isFilterDateMonthOnly = params.isFilterDateMonthOnly
                ? true
                : false;

            this.isShowFilter = params.isShowFilter ? true : false;
            this.isShowHomeLocationOptions =
                params.isShowHomeLocationOptions || false;
            this.data.species = params.species || [];
            this.data.diagnosis = params.diagnosis || [];
            this.data.diagnosisStatus = params.diagnosisStatus || 0;
            this.diagnosisShowMode = this.data.diagnosis.length > 0 ? 1 : 0;

            this.data.city = params.city || [];

            if (this.isFilterDateMonthOnly) {
                remove(this.dateRangeOptions, function (option) {
                    return ["2", "6"].includes(option.code);
                });
            }

            if (params.asOf) {
                this.data.asOf = moment(params.asOf);
            }
            if (params.start && params.end) {
                this.data.start = params.start;
                this.data.end = params.end;
            }
            this.datePickerOptions.format = params.datePickerOptions.format
                ? params.datePickerOptions.format
                : this.getDateFormat();
            this.datePickerOptions.timeZone = this._timeZone;

            if (this.isShowProvider) {
                this.getProviders();
            }

            if (this.isShowProviderOptions) {
                this.getProviders(false);
            }

            if (this.isShowItemClass) {
                if (params.itemClass) {
                    this.data.itemClass = params.itemClass;
                }
            }

            if (this.isShowItemStatus) {
                if (params.status != null && params.status != undefined) {
                    this.data.status = params.status;
                }
            }

            if (this.isShowItemCategory) {
                if (params.category) {
                    if (params.category.length > 0) {
                        this.data.category = params.category;
                        this.data.applyTo = 1;
                    }
                }
            }

            if (this.isShowModuleOption) {
                if (params.module) {
                    this.data.module = params.module;
                } else {
                    this.data.module = [this.moduleOptions[0]];
                }
            }

            // based on dateRangeOptions.code
            let datePickerOptionDefault = 2;
            if (params.datePickerOptionDefault) {
                datePickerOptionDefault = params.datePickerOptionDefault;
            } else if (this.datePickerOptions.format == "MMM YYYY") {
                datePickerOptionDefault = 3;
            }

            this.defaultSelected = params.datePickerRange
                ? params.datePickerRange
                : find(this.dateRangeOptions, {
                      code: String(datePickerOptionDefault)
                  });

            if (
                params.ignoreFormat &&
                this.datePickerOptions.format == "MMM YYYY"
            ) {
                this.datePickerOptions.format = this.getDateFormat();
            }

            if (this.isShowRemindersTypes) {
                let reminders = this.setDefaultReminderTypes();
                this.remindersTypesOptions = reminders;

                if (params.remindersType && params.remindersType.length > 0) {
                    this.data.remindersType = params.remindersType;
                } else {
                    this.data.remindersType = reminders; // select all by default
                }
            }

            this.isShowPatientStatus = params.isShowPatientStatus || false;
            if (
                this.isShowPatientStatus &&
                params.patientStatus &&
                params.patientStatus.length > 0
            ) {
                this.data.patientStatus = params.patientStatus;
            }

            if (this.isShowRemindersCategory) {
                this.fetchRemindersCategoryOptions();

                if (params.remindersCategory) {
                    this.data.remindersCategory = params.remindersCategory;
                } else {
                    this.data.remindersCategory = [];
                }
            }

            if (params.remindersNextVisit === 0) {
                this.remindersNextVisit = params.remindersNextVisit;
            } else {
                this.remindersNextVisit = 1;
            }

            if (this.reportId === CREPORT.TYPE.DIAGNOSIS_SUMMARY_LIST) {
                this.getDiagnosisFilterOptions();
            }

            if (this.reportId === CREPORT.TYPE.RABIES_SUMMARY) {
                this.getRabiesSummaryFilterOptions();
            }

            if (this.reportId === CREPORT.TYPE.WRITE_OFFS) {
                this.data.writeOffsMin = params.writeOffsMin || null;
                this.data.writeOffsMax = params.writeOffsMax || null;
            }

            this.isShowDateRangeDayMonth = params.isShowDateRangeDayMonth
                ? true
                : false;

            if (this.isShowDateRangeDayMonth) {
                let currentMonth = this._now().month;
                this.data.fromMonth = params.fromMonth
                    ? params.fromMonth
                    : currentMonth;
                this.data.fromDay = params.fromDay ? params.fromDay : 1;

                this.data.toMonth = params.toMonth
                    ? params.toMonth
                    : currentMonth;
                this.data.toDay = params.toDay ? params.toDay : this._now().day;
            }

            if (this.isAppointmentLogReport) {
                this.data.client = params?.client;
                this.data.patient = params?.patient;
            }

            if (params.filterByOptionDefault) {
                this.filterBySelected = find(this.filterByOptions, {
                    value: String(params.filterByOptionDefault)
                });
            }

            if (this.isShowCategorySales) {
                this.data.categorySales = this.convertToBoolean(
                    params.categorySales
                );
            }

            if (params.isShowAllSalesCategory) {
                this.data.isShowAllSalesCategory =
                    params.isShowAllSalesCategory;
            }

            if (
                params.isIncludeRefillSales != undefined &&
                params.isIncludeRefillSales != null
            ) {
                this.data.isIncludeRefillSales = params.isIncludeRefillSales;
            }

            if (
                params.isExcludeEligibilityFee != undefined &&
                params.isExcludeEligibilityFee != null
            ) {
                this.data.isExcludeEligibilityFee =
                    params.isExcludeEligibilityFee;
            }

            if (params.isShowHomeLocationOptions && params.home_location_id) {
                this.homeLocation = params.home_location_id;
            }

            this.$refs["filterDateRangeForm"].show();
        },
        setDefaultReminderTypes() {
            let rawTypes = this.REMINDER_TYPE;
            let arr = [];

            for (let key in rawTypes) {
                let value = rawTypes[key];

                if (value === 4) {
                    continue;
                }

                if (value === 5) {
                    key = "SMS (Text)";
                } else {
                    key = key[0].toUpperCase() + key.substr(1).toLowerCase();
                }

                arr.push({
                    value: value,
                    label: key
                });
            }

            return arr;
        },
        rangeChange(event) {
            this.skipUpdate = true;
            switch (parseInt(event.code)) {
                case 2:
                    // --- this week
                    this.data.start = moment()
                        .tz(this._timeZone)
                        .startOf("isoWeek");
                    this.data.end = moment()
                        .tz(this._timeZone)
                        .endOf("isoWeek");
                    break;
                case 3:
                    // --- this month
                    this.data.start = moment()
                        .tz(this._timeZone)
                        .startOf("month");
                    this.data.end = moment().tz(this._timeZone).endOf("month");
                    break;
                case 4:
                    // --- this quarter
                    this.data.start = moment()
                        .tz(this._timeZone)
                        .startOf("quarter");
                    this.data.end = moment()
                        .tz(this._timeZone)
                        .endOf("quarter");
                    break;
                case 5:
                    // --- this year
                    this.data.start = moment()
                        .tz(this._timeZone)
                        .startOf("year");
                    this.data.end = moment().tz(this._timeZone).endOf("year");
                    break;
                case 6:
                    // --- this year
                    this.data.start = moment()
                        .tz(this._timeZone)
                        .subtract(1, "weeks")
                        .startOf("isoWeek");
                    this.data.end = moment()
                        .tz(this._timeZone)
                        .subtract(1, "weeks")
                        .endOf("isoWeek");
                    break;
                case 7:
                    // --- this year
                    this.data.start = moment()
                        .tz(this._timeZone)
                        .subtract(1, "months")
                        .startOf("month");
                    this.data.end = moment()
                        .tz(this._timeZone)
                        .subtract(1, "months")
                        .endOf("month");
                    break;
                case 8:
                    // --- this year
                    this.data.start = moment()
                        .tz(this._timeZone)
                        .subtract(1, "Q")
                        .startOf("quarter");
                    this.data.end = moment()
                        .tz(this._timeZone)
                        .subtract(1, "Q")
                        .endOf("quarter");
                    break;
                case 9:
                    // --- last year
                    this.data.start = moment()
                        .tz(this._timeZone)
                        .subtract(1, "years")
                        .startOf("year");
                    this.data.end = moment()
                        .tz(this._timeZone)
                        .subtract(1, "years")
                        .endOf("year");
                    break;
                default:
                    break;
            }
        },
        dateChange(event, element) {
            this.data[element] = event.date;
            // --- if value change from select, skip the function to set default selected
            if (!this.skipUpdate) {
                this.defaultSelected = find(this.dateRangeOptions, {
                    code: "1"
                });
            }

            if (this.skipUpdate && element === "end") {
                this.skipUpdate = false;
            }
        },
        async filterDate() {
            // --- variables with default date
            let fromDate = "";
            let toDate = "";
            let asOf = "";
            const clientIds = [];
            const patientIds = [];
            let proceedFilter = true;

            if (this.isShowDateTimeRange) {
                fromDate = this.data.start.unix();
                toDate = this.data.end.unix();
            } else if (this.isShowAsOf) {
                asOf = this.data.asOf;
            } else {
                fromDate = this.data.start
                    ? this.data.start.startOf("days").unix()
                    : null;
                toDate = this.data.end
                    ? this.data.end.endOf("days").unix()
                    : null;
            }

            if (this.isShowItemCategory) {
                if (this.data.applyTo == 0) {
                    this.data.category = [];
                }
            }

            switch (this.datePickerOptions.format) {
                case "MMM YYYY":
                    if (!this.data.ignoreFormat) {
                        fromDate = this.data.start.startOf("months").unix();
                        toDate = this.data.end.endOf("months").unix();
                    }
                    break;
                default:
                    break;
            }

            switch (parseInt(this.$route.params.id)) {
                case CREPORT.TYPE.APPOINTMENT_LOG: {
                    if (
                        this.data?.client &&
                        Object.keys(this.data.client).length > 0
                    ) {
                        clientIds.push(this.data.client.id);
                    }
                    if (
                        this.data?.patient &&
                        Object.keys(this.data.patient).length > 0
                    ) {
                        patientIds.push(this.data.patient.id);
                    }
                    break;
                }

                case CREPORT.TYPE.WRITE_OFFS: {
                    const valid = await this.$refs.filterWriteOffs.validate();
                    if (!valid) {
                        proceedFilter = false;
                    }
                }
            }

            if (!proceedFilter) {
                return;
            }

            this.$root.$emit("report-filter-date-range:refresh", {
                reportId: this.reportId,
                from: fromDate,
                to: toDate,
                start: this.data.start,
                end: this.data.end,
                datePickerRange: this.defaultSelected,
                provider: this.data.provider,
                categorySales: this.data.categorySales,
                itemClass: this.data.itemClass,
                category: this.data.category,
                status: this.data.status,
                clientIds: clientIds,
                patientIds: patientIds,
                asOf: asOf,
                module: this.data.module,
                refresh: 2,
                startOfWeek: moment().tz(this._timeZone).startOf("week").unix(),
                items: this.data.items,
                itemShowMode: this.itemShowMode,
                providerShowMode: this.data.providerShowMode,
                remindersCategoryMode: this.remindersCategoryMode,
                remindersCategory: this.data.remindersCategory,
                remindersType: this.data.remindersType,
                remindersNextVisit: this.remindersNextVisit,
                patientStatus: this.data.patientStatus,
                species: this.data.species,
                diagnosis: this.data.diagnosis,
                diagnosisStatus: this.data.diagnosisStatus,
                city: this.data.city,
                fromMonth: this.data.fromMonth,
                fromDay: this.data.fromDay,
                toMonth: this.data.toMonth,
                toDay: this.data.toDay,
                filterBy: this.filterBySelected,
                isShowAllSalesCategory: this.data.isShowAllSalesCategory,
                isIncludeRefillSales: this.data.isIncludeRefillSales,
                isExcludeEligibilityFee: this.data.isExcludeEligibilityFee,
                home_location_id: this.homeLocation,
                home_location: this.homeLocations.find(
                    e => e.id == this.homeLocation
                ),
                writeOffsMin: this.data.writeOffsMin,
                writeOffsMax: this.data.writeOffsMax
            });

            this.$refs["filterDateRangeForm"].hide();
        },
        handleChildChanges(payload) {
            this.data = {
                ...this.data,
                ...payload
            };
        },
        getProviders(autoSelect = true) {
            let self = this;
            self.optionLoading.providerList = true;

            return this.$http
                .get(process.env.BARRAMUNDI + "actor/staff/business/providers")
                .then(function (response) {
                    self.providerList = get(
                        response,
                        "data.response.resource.data",
                        []
                    );

                    self.providerList = map(self.providerList, element => ({
                        value: element.actor.id,
                        name:
                            element.actor.firstname +
                            " " +
                            element.actor.lastname
                    }));

                    // --- sort by provider name
                    self.providerList = sortBy(self.providerList, ["name"]);
                    delete self.optionLoading["providerList"];

                    // auto select first option
                    if (autoSelect) {
                        self.data.provider = [self.providerList[0]];
                    }
                });
        },

        getDiagnosisFilterOptions() {
            let self = this;
            self.diagnosisList = [];
            self.speciesList = [];

            self.optionLoading.diagnosisList = true;
            self.optionLoading.speciesList = true;

            return this.$http
                .get(
                    process.env.BARRAMUNDI +
                        "report/reports/diagnosis-summary/options"
                )
                .then(res => {
                    const response = res.data.response;

                    if (response.resource.diagnosis) {
                        self.diagnosisList = map(
                            response.resource.diagnosis,
                            diagnosis => ({
                                value: diagnosis.id,
                                name: diagnosis.term
                            })
                        );
                        self.diagnosisList = sortBy(self.diagnosisList, [
                            "name"
                        ]);
                        delete self.optionLoading["diagnosisList"];
                    }

                    if (response.resource.species) {
                        self.speciesList = map(
                            response.resource.species,
                            species => ({
                                value: species.id,
                                name: species.value
                            })
                        );

                        self.speciesList = sortBy(self.speciesList, ["name"]);
                        delete self.optionLoading["speciesList"];
                        if (self.data.species.length == 0) {
                            self.data.species = self.speciesList;
                        }
                    }
                });
        },
        getRabiesSummaryFilterOptions() {
            let self = this;
            self.cityList = [];
            self.speciesList = [];
            self.optionLoading.cityList = true;
            self.optionLoading.speciesList = true;

            return this.$http
                .get(
                    process.env.BARRAMUNDI +
                        "report/reports/rabies-summary/options"
                )
                .then(res => {
                    const response = res.data.response;

                    if (response.resource.city) {
                        self.cityList = map(response.resource.city, city => ({
                            value: city,
                            name: city
                        }));
                        self.cityList = sortBy(self.cityList, ["name"]);
                        delete self.optionLoading["cityList"];
                    }

                    if (response.resource.species) {
                        self.speciesList = map(
                            response.resource.species,
                            (species, id) => ({
                                value: id,
                                name: species
                            })
                        );

                        self.speciesList = sortBy(self.speciesList, ["name"]);
                        delete self.optionLoading["speciesList"];
                        if (self.data.species.length == 0) {
                            self.data.species = self.speciesList;
                        }
                    }
                });
        },
        handlePrint() {
            this.processReport(1);
        },
        handleExport(type) {
            this.processReport(0, type);
        },
        async processReport(option, type) {
            let parameters = {};

            this.isExporting = true;

            parameters = {
                from: this.data.start.unix(),
                to: this.data.end.unix(),
                business_id: this.$root.setting.business_id,
                user_id: this.$root.currentActor.id,
                timezone: this._timeZone,
                exportType: type,
                filterBy: this.filterBySelected.value
            };

            if (this.reportId == CREPORT.TYPE.SALES_BY_PROVIDER_SUMMARY) {
                parameters["isFromExport"] = 1;
            }

            if (this.reportId == CREPORT.TYPE.SALES_BY_PROVIDER_DETAILS) {
                parameters["item_class"] = this.data.itemClass;
                parameters["filter_by_sales"] = this.data.categorySales;
                parameters["isFromExport"] = 1;
                parameters["category"] = this.data.category
                    ? this.data.category
                    : [];
                if (this.data.provider) {
                    parameters["provider"] = this.data.provider.map(
                        provider => provider.value
                    );
                }
            }

            if (this.reportId == CREPORT.TYPE.DELETED_TRANSACTIONS) {
                parameters["isFromExport"] = 1;
                parameters["module"] = this.data.module.map(id => id.value);
            }

            await this.exportReport({ option, parameters });
            this.$refs["filterDateRangeForm"].hide();
        }
    }
};
</script>
